import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy, signal, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';
import { Card } from 'src/providers/card/card';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Requests } from 'src/providers/requests/requests';
import { BookJob } from 'src/providers/book-job/book-job';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Workflows } from 'src/providers/workflows/workflows';
import { Client } from 'src/providers/client/client';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Events } from 'src/providers/events/events';
import { WindowService } from 'src/shared/providers/window.service';
import { getCardOptions } from 'src/old-pages/booking/confirm/confirm-card-options';
import { ThemeService } from 'src/shared/services/theme.service';

interface AutopilotCheckItem {
  id: string;
  label: string;
  status: 'complete' | 'incomplete' | 'warning';
  description?: string;
}

interface AutopilotState {
  cardAdded: boolean;
  cardBrand?: string;
  cardLast4?: string;
  callDecisionMade?: boolean;
}

interface PendingAutopilotAction {
  type: 'automatic-booking' | 'job-request' | 'agent-enable' | 'workflow-publish' | 'workflow-enable';
  payload: any;
  successMessage: string;
  successRoute: string;
}

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './add-credit-card-to-book.component.html'
})
export class AddCreditCardToBookComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cardInfo', { static: false }) cardInfo!: ElementRef;

  // Mock automation percentage (in production, would be calculated from actual data)
  automationPercentage = signal(35);

  cardAdded = signal(false);
  cardBrand = signal('');
  cardLast4 = signal('');
  isAddingCard = signal(false);
  stripeReady = signal(false);
  callDecisionMade = signal(false);
  errorMessage = '';

  checklistItems = signal<AutopilotCheckItem[]>([
    {
      id: 'pros-added',
      label: 'Pros Added',
      status: 'complete',
      description: '3 pros on your priority list'
    },
    {
      id: 'workflows-configured',
      label: 'Workflows Configured',
      status: 'complete',
      description: '2 workflows created'
    },
    {
      id: 'checklists-created',
      label: 'Checklists Created',
      status: 'warning',
      description: '1 checklist (recommend at least 2)'
    },
    {
      id: 'integrations-connected',
      label: 'Integrations Connected',
      status: 'complete',
      description: 'Airbnb calendar synced'
    }
  ]);

  private cardEl: any;
  private cardHandler = this.onChange.bind(this);
  pendingAction: PendingAutopilotAction | null = null;

  constructor(
    private rightSidePanel: RightSidePanelService,
    private storage: TidyStorage,
    private cdr: ChangeDetectorRef,
    private util: Util,
    private navCtrl: CustomNavController,
    private stripeProvider: StripeProvider,
    private card: Card,
    private guestReservations: GuestReservations,
    private requests: Requests,
    private bookJob: BookJob,
    private agentsProvider: AgentsProvider,
    private workflowProvider: Workflows,
    private client: Client,
    private onboardingProvider: OnboardingProvider,
    private events: Events,
    private themeService: ThemeService,
    public windowService: WindowService
  ) {}

  ngOnInit() {
    this.rightSidePanel.setDialogPageTitle('Add Payment Method to Book');
    this.loadState();
    this.loadPendingAction();
  }

  async loadPendingAction() {
    this.pendingAction = await this.storage.retrieve('autopilot-pending-action') as PendingAutopilotAction;
  }

  ngAfterViewInit() {
    this.initiateCardElement();
  }

  ngOnDestroy() {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
  }

  initiateCardElement(): void {
    this.stripeReady.set(false);
    setTimeout(() => {
      this.cardEl = this.stripeProvider.getElements().create('card', getCardOptions(!this.themeService.isDarkMode()));
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
      this.cardEl.on('ready', () => {
        this.stripeReady.set(true);
        this.cdr.markForCheck();
      });
    }, 100);
  }

  onChange(err: any): void {
    this.errorMessage = err.error ? err.error.message : '';
    this.cdr.markForCheck();
  }

  async loadState() {
    const state = await this.storage.retrieve('autopilot-demo-state') as AutopilotState;
    if (state?.cardAdded) {
      this.cardAdded.set(true);
      this.cardBrand.set(state.cardBrand || '');
      this.cardLast4.set(state.cardLast4 || '');
    }
    if (state?.callDecisionMade) {
      this.callDecisionMade.set(true);
    }
  }

  getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      complete: 'assets/img/checkmark-green.svg',
      incomplete: 'assets/img/circle-outline.svg',
      warning: 'assets/img/alert.svg'
    };
    return icons[status] || icons.incomplete;
  }

  openPricingPage() {
    window.open('https://tidy.com/pricing', '_blank');
  }

  async skipCall() {
    this.callDecisionMade.set(true);
    await this.saveDecisionState();
    this.cdr.markForCheck();
  }

  async scheduleCall() {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  private async saveDecisionState() {
    const existingState = await this.storage.retrieve('autopilot-demo-state') as AutopilotState || {};
    await this.storage.save('autopilot-demo-state', {
      ...existingState,
      callDecisionMade: true
    } as AutopilotState);
  }

  async goLive() {
    try {
      this.isAddingCard.set(true);
      this.errorMessage = '';
      this.cdr.markForCheck();

      // If card not already added, create and save it first
      if (!this.cardAdded()) {
        // Create Stripe token
        const { token, error } = await this.stripeProvider
          .getInstance()
          .createToken(this.cardEl);

        if (error) {
          this.errorMessage = error.message || 'Error creating card token';
          this.isAddingCard.set(false);
          this.cdr.markForCheck();
          return;
        }

        if (!token) {
          this.errorMessage = 'Failed to create card token';
          this.isAddingCard.set(false);
          this.cdr.markForCheck();
          return;
        }

        // Save card to backend
        await this.card.saveCard({
          type: 'card',
          stripe_card_token: token.id,
          primary_payment_method: true,
        });
      }

      // Set hasCard flag
      localStorage.setItem('hasCard', 'true');

      // Execute pending action if exists
      if (this.pendingAction) {
        await this.executePendingAction();
        return;
      }
      
      // Clean up state and close the panel
      await this.storage.delete('autopilot-demo-state');
      this.rightSidePanel.closeRightSidePanel();

      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    } catch (error: any) {
      this.errorMessage = error.message || 'An error occurred';
      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    }
  }

  private async executePendingAction(): Promise<void> {
    try {
      const { type, payload, successMessage, successRoute } = this.pendingAction;

      switch (type) {
        case 'automatic-booking':
          await this.guestReservations.updateManyAddressAutomaticBooking(payload.automaticBookingPayload);
          await this.onboardingProvider.markGoalAsCompleted('enableAutomaticBooking');
          this.onboardingProvider.setShowOnboardingOnPage(false);
          this.onboardingProvider.setShowOnboardingInHeader(true);
          await this.client.getAddresses(true);
          await this.storage.save('shouldReloadAllAutomations', true);
          break;

        case 'job-request':
          if (payload.requestType === 'assign') {
            await this.bookJob.saveJob(payload.bookingType, payload.jobPayload, null, null);
          } else if (payload.requestType === 'all_my_pros') {
            await this.requests.addJobRequest(payload.addressId, payload.jobPayload);
          } else {
            await this.requests.addProRequest(payload.addressId, payload.jobPayload);
          }
          localStorage.setItem('madeUpdateToSchedule', 'true');
          localStorage.setItem('didBookJob', 'true');
          break;

        case 'agent-enable':
          await this.agentsProvider.updateAgentSettings(payload.agentKey, payload.agentPayload);
          const agentSettings = await this.agentsProvider.getAgentSettings();
          const updatedAgentSettings = agentSettings.map(agent =>
            agent.key === payload.agentKey ? payload.agentPayload : agent
          );
          await this.storage.save('agentSettings', updatedAgentSettings);
          this.events.publish('agent:settings:updated');
          if (payload.onboardingFlowName) {
            await this.onboardingProvider.markGoalAsCompleted(payload.onboardingFlowName);
          }
          break;

        case 'workflow-publish':
          await this.workflowProvider.publishWorkflow(payload.versionId);
          await this.workflowProvider.unpauseWorkflow(payload.workflowId);
          break;

        case 'workflow-enable':
          await this.workflowProvider.unpauseWorkflow(payload.workflowId);
          break;
      }

      // Clean up
      await this.storage.delete('autopilot-pending-action');
      await this.storage.delete('autopilot-demo-state');

      // Show success and navigate
      this.util.showSuccess(successMessage);
      this.rightSidePanel.closeRightSidePanel();
      this.navCtrl.navigateForward(successRoute);

      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    } catch (error: any) {
      this.errorMessage = error.message || 'An error occurred while completing the action';
      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    }
  }
}
